import { ChunkData, ServerMessagePayload } from '@common/api';
import { Graphics } from 'pixi.js';

import { ClientGlobalSystem } from './ClientBase';

const BOUNDARY_COLOR = 0xff00ff; // Magenta
const BOUNDARY_ALPHA = 0.8;
const BOUNDARY_LINE_WIDTH = 2;

/**
 * Debug system that renders map chunk boundaries overlay.
 * Toggled via the Debug Panel.
 */
export class DebugChunkBoundariesRenderSystem extends ClientGlobalSystem {
  private graphics: Graphics | null = null;
  private initialized = false;
  private lastShowState = false;
  private lastChunkKeys = '';

  run(): ServerMessagePayload[] {
    const world = this.getWorld();
    const chunkDataService = this.clientServices.chunkData;
    const showChunkBoundaries = this.clientServices.debug.state.showChunkBoundaries;

    // Handle visibility toggle
    if (this.graphics) {
      this.graphics.visible = showChunkBoundaries;
    }

    // Early exit if nothing to show
    if (!showChunkBoundaries) {
      if (this.lastShowState) {
        this.lastShowState = false;
        this.lastChunkKeys = '';
      }
      return [];
    }

    // Get local player's scene
    const localEntityId = this.clientServices.localPlayer.entityId;
    const localPlayerTransform = localEntityId
      ? world.getComponent(localEntityId, 'Transform')
      : null;
    const currentSceneId = localPlayerTransform?.sceneId;
    if (!currentSceneId) return [];

    // Get chunks
    const chunks = chunkDataService.getChunks(currentSceneId);
    if (!chunks) return [];

    // Create graphics if not exists
    if (!this.graphics) {
      this.graphics = new Graphics();
      this.graphics.zIndex = 99999; // Above grid lines, below collision grid
      this.clientServices.render.stage?.addChild(this.graphics);
    }

    // Optimization: only redraw if show state changed OR the set of loaded chunks changed
    const currentChunkKeys = Array.from(chunks.keys()).sort().join(',');
    const needsRedraw =
      !this.initialized ||
      this.lastShowState !== showChunkBoundaries ||
      this.lastChunkKeys !== currentChunkKeys;

    if (!needsRedraw) return [];

    this.initialized = true;
    this.lastShowState = showChunkBoundaries;
    this.lastChunkKeys = currentChunkKeys;

    // Get scene metadata to get chunkSize and tile dimensions
    const metadata = chunkDataService.getSceneMetadata(currentSceneId);
    if (!metadata) return [];

    this.renderBoundaries(chunks, metadata.chunkSize, metadata.tileWidth, metadata.tileHeight);

    return [];
  }

  /**
   * Renders the boundaries for all currently loaded chunks.
   */
  private renderBoundaries(
    chunks: Map<string, ChunkData>,
    chunkSize: number,
    tileWidth: number,
    tileHeight: number,
  ): void {
    if (!this.graphics) return;

    const chunkPixelWidth = chunkSize * tileWidth;
    const chunkPixelHeight = chunkSize * tileHeight;

    this.graphics.clear();

    // Draw boundaries for all loaded chunks
    for (const [, chunkData] of chunks) {
      const chunkWorldX = chunkData.chunkX * chunkPixelWidth;
      const chunkWorldY = chunkData.chunkY * chunkPixelHeight;

      this.graphics.rect(chunkWorldX, chunkWorldY, chunkPixelWidth, chunkPixelHeight);
    }

    this.graphics.stroke({
      color: BOUNDARY_COLOR,
      alpha: BOUNDARY_ALPHA,
      width: BOUNDARY_LINE_WIDTH,
    });
  }
}
